import type { ClinicalSeverityData, ConservativeTherapyEntry } from '@/lib/clinicalSeverityTypes';

const DATE_PATTERN = String.raw`(?:\d{1,2}/\d{1,2}/\d{4}|\d{4}-\d{2}-\d{2})`;
const KNEE_MUA_FUNCTION_PATTERN = /(reciprocal\s+stair|stair\s+climb|stairs|rising\s+from|rise\s+from|standard\s+seated|chair|activities of daily living|\badl\b|without\s+assistance|mechanical\s+block)/i;

function normalize(text: string | null | undefined): string {
  return (text || '').toLowerCase();
}

function hasText(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return new Date(Date.UTC(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3])));
  }
  const usMatch = value.match(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/);
  if (usMatch) {
    return new Date(Date.UTC(Number(usMatch[3]), Number(usMatch[1]) - 1, Number(usMatch[2])));
  }
  return null;
}

function daysBetween(start: Date, end: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((end.getTime() - start.getTime()) / msPerDay);
}

function isPhysicalTherapyEntry(entry: ConservativeTherapyEntry): boolean {
  return /\b(pt|physical therapy|therapy|rehab|rehabilitation)\b/i.test(entry.modality || '');
}

function therapyEntryElapsedDays(entry: ConservativeTherapyEntry): number | null {
  const start = parseDateOnly(entry.start_date);
  const end = parseDateOnly(entry.end_date);
  if (!start || !end) return null;
  return Math.abs(daysBetween(start, end));
}

function entryHasTherapyTimeline(entry: ConservativeTherapyEntry): boolean {
  const start = parseDateOnly(entry.start_date);
  const end = parseDateOnly(entry.end_date);
  if (start && end && Math.abs(daysBetween(start, end)) >= 27) return true;
  if (typeof entry.duration_weeks === 'number' && entry.duration_weeks >= 4) return true;
  return /\b(?:\d{1,2}|six|eight|twelve|thirteen)\s*(?:weeks?|wks?)\b/i.test(entry.duration || '');
}

function entryHasFailedOutcome(entry: ConservativeTherapyEntry): boolean {
  return /(failed|failure|plateau|persistent|persisted|despite|unable|without durable|no sustained)/i.test(entry.outcome || '');
}

function bmiValueFromText(text: string): number | null {
  const match = text.match(/\b(?:bmi|body mass index)\b\s*(?:is|=|of|:)?\s*(\d+(?:\.\d+)?)/i);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
}

function degreeNumbers(value: string | null | undefined): number[] {
  return Array.from((value || '').matchAll(/\b(\d{1,3})\b/g))
    .map(match => Number(match[1]))
    .filter(value => Number.isFinite(value));
}

function hasMuaFlexionLimit(value: string | null | undefined): boolean {
  const numbers = degreeNumbers(value);
  if (numbers.length === 0) return false;
  return Math.max(...numbers) <= 110;
}

function extractKneeFlexionFromText(text: string): string | null {
  const range = text.match(/\bflexion[^.\n]{0,120}?(\d{2,3})\s*(?:to|-)\s*(\d{2,3})\s*(?:degrees?|°)?/i);
  if (range) return `${range[1]} to ${range[2]}`;
  const single = text.match(/\bflexion[^.\n]{0,120}?(\d{2,3})\s*(?:degrees?|°)/i);
  return single?.[1] || null;
}

function normalizeFlexionForNarrative(value: string | null | undefined): string | null {
  if (!hasText(value)) return null;
  return value
    .replace(/\s*(?:degrees?|°)\b/gi, '')
    .replace(/\s*-\s*/g, ' to ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeAdlImpact(value: string | null | undefined): string | null {
  if (!hasText(value)) return null;
  const cleaned = value
    .replace(/^this\s+specific\s+contracture\s+(?:explicitly\s+)?prevents\s+/i, '')
    .replace(/^the\s+fixed\s+flexion\s+limitation\s+prevents\s+/i, '')
    .replace(/^prevents\s+/i, '')
    .replace(/^is\s+unable\s+to\s+perform\s+/i, '')
    .replace(/\s+/g, ' ')
    .replace(/[.;]\s*$/, '')
    .trim();
  return cleaned || null;
}

function normalizeAdlImpactSentence(value: string | null | undefined): string | null {
  if (!hasText(value)) return null;
  const cleaned = value
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return null;
  return /[.!?]$/.test(cleaned) ? cleaned : `${cleaned}.`;
}

function therapyDurationDaysFromText(text: string): number | null {
  const explicitMatches = Array.from(text.matchAll(/\b(\d{2,3})[-\s]*(?:days?|day)\b/gi));
  const therapyMatch = explicitMatches.find(match => {
    const start = Math.max(0, (match.index || 0) - 160);
    const end = Math.min(text.length, (match.index || 0) + match[0].length + 160);
    return /(formal|supervised|in[- ]person|physical therapy|\bpt\b|rehabilitation|therapy|conservative management)/i.test(text.slice(start, end));
  }) || explicitMatches[0];
  if (therapyMatch) {
    const value = Number(therapyMatch[1]);
    if (Number.isFinite(value)) return value;
  }

  const datePattern = String.raw`(\d{1,2}/\d{1,2}/\d{4}|\d{4}-\d{2}-\d{2})`;
  const range = text.match(new RegExp(String.raw`(?:formal|supervised|in[- ]person|physical therapy|\bpt\b|rehabilitation|therapy)[^.\n]{0,220}(?:from|between)?\s*${datePattern}[^.\n]{0,80}(?:through|to|-|and)\s*${datePattern}`, 'i')) ||
    text.match(new RegExp(String.raw`${datePattern}[^.\n]{0,80}(?:through|to|-|and)\s*${datePattern}[^.\n]{0,180}(?:formal|supervised|in[- ]person|physical therapy|\bpt\b|rehabilitation|therapy)`, 'i'));
  if (!range) return null;
  const start = parseDateOnly(range[1]);
  const end = parseDateOnly(range[2]);
  if (!start || !end) return null;
  return Math.abs(daysBetween(start, end));
}

export function clinicalSeverityEvidenceText(data?: ClinicalSeverityData | null): string {
  if (!data) return '';

  const parts: string[] = [];
  const imaging = data.radiographic_findings;
  if (imaging) {
    parts.push([
      'Structured radiographic evidence:',
      imaging.imaging_modality,
      imaging.imaging_date ? `dated ${imaging.imaging_date}` : null,
      imaging.severity_grade ? `Severity: ${imaging.severity_grade}` : null,
      imaging.specific_findings?.length ? `Findings: ${imaging.specific_findings.join('; ')}` : null,
    ].filter(Boolean).join(' '));
  }

  for (const entry of data.conservative_therapy || []) {
    const range = [entry.start_date, entry.end_date].filter(Boolean).join(' through ');
    const duration = entry.duration_weeks ? `${entry.duration_weeks} weeks` : entry.duration;
    parts.push([
      'Structured conservative therapy evidence:',
      entry.modality,
      range ? `from ${range}` : null,
      duration ? `for ${duration}` : null,
      entry.outcome ? `Outcome: ${entry.outcome}` : null,
    ].filter(Boolean).join(' '));
  }

  for (const injection of data.injection_history || []) {
    parts.push([
      'Structured injection evidence:',
      injection.type,
      injection.date ? `on ${injection.date}` : null,
      injection.location ? `at ${injection.location}` : null,
      injection.outcome ? `Outcome: ${injection.outcome}` : null,
    ].filter(Boolean).join(' '));
  }

  if (typeof data.bmi === 'number') {
    parts.push(`Structured BMI evidence: BMI of ${data.bmi}.`);
  }

  if (data.comorbidities?.length) {
    parts.push(`Structured comorbidity evidence: ${data.comorbidities.join('; ')}.`);
  }

  const rom = data.rom_measurements;
  if (rom?.flexion || rom?.extension || rom?.other_measurements?.length) {
    parts.push([
      'Structured ROM evidence:',
      rom.flexion ? `flexion ${rom.flexion}` : null,
      rom.extension ? `extension ${rom.extension}` : null,
      rom.other_measurements?.length ? `measurements ${rom.other_measurements.join('; ')}` : null,
    ].filter(Boolean).join(' '));
  }

  if (data.functional_limitations?.length) {
    parts.push(`Structured functional limitation evidence: ${data.functional_limitations.join('; ')}.`);
  }

  const kneeMua = data.knee_mua;
  if (kneeMua) {
    const ptRange = [kneeMua.pt_start_date, kneeMua.pt_end_date].filter(Boolean).join(' through ');
    parts.push([
      'Structured knee MUA evidence:',
      kneeMua.pt_duration_days ? `PT duration ${kneeMua.pt_duration_days} days` : null,
      kneeMua.pt_duration_weeks ? `PT duration ${kneeMua.pt_duration_weeks} weeks` : null,
      ptRange ? `from ${ptRange}` : null,
      kneeMua.rom_flexion ? `flexion ${kneeMua.rom_flexion}` : null,
      kneeMua.rom_extension ? `extension ${kneeMua.rom_extension}` : null,
      kneeMua.adl_impact ? `functional impact: ${kneeMua.adl_impact}` : null,
      kneeMua.static_rom_dates?.length ? `static ROM dates: ${kneeMua.static_rom_dates.join(', ')}` : null,
    ].filter(Boolean).join(' '));
  }

  return parts.join('\n');
}

export function hasDefensibleFormalTherapyTimeline(
  text: string,
  data?: ClinicalSeverityData | null,
): boolean {
  const timelineInSentence =
    new RegExp(String.raw`(?:formal|supervised|in[- ]person|physical therapy|pt|rehabilitation)[^.\n]{0,220}${DATE_PATTERN}[^.\n]{0,160}${DATE_PATTERN}`, 'i').test(text) ||
    new RegExp(String.raw`${DATE_PATTERN}[^.\n]{0,180}${DATE_PATTERN}[^.\n]{0,140}(?:formal|supervised|therapy|pt|rehabilitation)`, 'i').test(text);
  const labeledTherapyDates =
    new RegExp(String.raw`therapy\s+start\s+date\s*:\s*${DATE_PATTERN}`, 'i').test(text) &&
    new RegExp(String.raw`therapy\s+end\s+date\s*:\s*${DATE_PATTERN}`, 'i').test(text) &&
    /(formal|supervised|in[- ]person|physical therapy|\bpt\b|rehabilitation)/i.test(text);
  const structuredTherapyDates = (data?.conservative_therapy || [])
    .filter(isPhysicalTherapyEntry)
    .some(entryHasTherapyTimeline);

  return timelineInSentence || labeledTherapyDates || structuredTherapyDates;
}

export function hasShoulderFormalTherapyFailure(
  text: string,
  data?: ClinicalSeverityData | null,
): boolean {
  const lower = normalize(text);
  const hasTimeline = hasDefensibleFormalTherapyTimeline(text, data);
  const failedInText = /(failed|failure|plateau|persistent|persisted|despite|without durable|no sustained)/i.test(lower);
  const failedInStructured = (data?.conservative_therapy || [])
    .filter(isPhysicalTherapyEntry)
    .some(entryHasFailedOutcome);

  return hasTimeline && (failedInText || failedInStructured);
}

export function hasAscExclusionSiteOfCareJustification(
  text: string,
  data?: ClinicalSeverityData | null,
): boolean {
  const combined = [text, clinicalSeverityEvidenceText(data)].filter(Boolean).join('\n');
  const lower = normalize(combined);
  const bmi = data?.bmi ?? bmiValueFromText(combined);
  const highBmi = typeof bmi === 'number' && bmi > 40;
  const airwayRisk = /(severe\s+)?(?:obstructive\s+)?sleep apnea|\bosa\b|cpap|airway compromise|airway/i.test(combined);
  const siteLanguage = /(ambulatory surgery center|\basc\b|hospital[- ]based outpatient|hospital outpatient|hospital setting|st\.?\s*elizabeth)/i.test(combined);
  const contraindicationLanguage = /(contraindicated|unsafe|not appropriate|high risk|must be treated|required|airway compromise)/i.test(combined);

  return highBmi && airwayRisk && siteLanguage && contraindicationLanguage && (
    lower.includes('sleep apnea') ||
    lower.includes('osa') ||
    lower.includes('cpap')
  );
}

export function hasKneeMuaFunctionalLimitation(
  text: string,
  data?: ClinicalSeverityData | null,
): boolean {
  if (KNEE_MUA_FUNCTION_PATTERN.test(text)) return true;
  if (hasText(data?.knee_mua?.adl_impact) && KNEE_MUA_FUNCTION_PATTERN.test(data.knee_mua.adl_impact)) return true;
  return (data?.functional_limitations || []).some(item => KNEE_MUA_FUNCTION_PATTERN.test(item));
}

export function hasKneeMuaTherapyCourse(
  text: string,
  data?: ClinicalSeverityData | null,
): boolean {
  const kneeMua = data?.knee_mua;
  if (typeof kneeMua?.pt_duration_days === 'number' && kneeMua.pt_duration_days >= 77) return true;
  if (typeof kneeMua?.pt_duration_weeks === 'number' && kneeMua.pt_duration_weeks >= 12) return true;

  const kneeStart = parseDateOnly(kneeMua?.pt_start_date);
  const kneeEnd = parseDateOnly(kneeMua?.pt_end_date);
  if (kneeStart && kneeEnd && Math.abs(daysBetween(kneeStart, kneeEnd)) >= 77) return true;

  const structuredTherapyClears = (data?.conservative_therapy || [])
    .filter(isPhysicalTherapyEntry)
    .some(entry => {
      const elapsedDays = therapyEntryElapsedDays(entry);
      return (typeof entry.duration_weeks === 'number' && entry.duration_weeks >= 12) ||
        (typeof elapsedDays === 'number' && elapsedDays >= 77) ||
        /\b(?:12th|twelfth|12[- ]week|twelve[- ]week|77[- ]day|77\s+days)\b/i.test(entry.duration || '');
    });
  if (structuredTherapyClears) return true;

  const combined = [text, clinicalSeverityEvidenceText(data)].filter(Boolean).join('\n');
  const therapyWithDuration = /\b(?:12th|twelfth|12[- ]week|twelve[- ]week|77[- ]day|77\s+days)\b/i.test(combined);
  const therapyTimeline = hasDefensibleFormalTherapyTimeline(combined, data);
  return therapyWithDuration && therapyTimeline;
}

export function hasKneeMuaRomEvidence(
  text: string,
  data?: ClinicalSeverityData | null,
): boolean {
  const kneeMua = data?.knee_mua;
  const flexion = kneeMua?.rom_flexion || data?.rom_measurements?.flexion || extractKneeFlexionFromText(text);
  if (!hasMuaFlexionLimit(flexion)) return false;

  const structuredDates = kneeMua?.static_rom_dates?.length || 0;
  const otherMeasurements = data?.rom_measurements?.other_measurements || [];
  const datedStructuredMeasurements = otherMeasurements.filter(item => new RegExp(DATE_PATTERN, 'i').test(item)).length;
  if (structuredDates >= 3 || datedStructuredMeasurements >= 3) return true;

  const combined = [text, clinicalSeverityEvidenceText(data)].filter(Boolean).join('\n');
  const dateMatches = combined.match(new RegExp(DATE_PATTERN, 'g')) || [];
  return /(consecutive|static|plateau|measurements?|remained\s+static|rigidly\s+fixed)/i.test(combined) &&
    dateMatches.length >= 3;
}

export function kneeMuaNarrativeVariables(
  text: string,
  data?: ClinicalSeverityData | null,
): { ptDurationDays: string; romFlexion: string; adlImpact: string } {
  const kneeMua = data?.knee_mua;
  let ptDurationDays = typeof kneeMua?.pt_duration_days === 'number'
    ? String(kneeMua.pt_duration_days)
    : null;

  if (!ptDurationDays) {
    const start = parseDateOnly(kneeMua?.pt_start_date);
    const end = parseDateOnly(kneeMua?.pt_end_date);
    if (start && end) {
      ptDurationDays = String(Math.abs(daysBetween(start, end)));
    }
  }

  if (!ptDurationDays) {
    const structuredEntry = (data?.conservative_therapy || [])
      .filter(isPhysicalTherapyEntry)
      .find(entry => therapyEntryElapsedDays(entry) || entry.duration);
    const elapsed = structuredEntry ? therapyEntryElapsedDays(structuredEntry) : null;
    ptDurationDays = elapsed
      ? String(elapsed)
      : structuredEntry?.duration?.match(/\b(\d{2,3})[-\s]*(?:days?|day)\b/i)?.[1] || null;
  }

  if (!ptDurationDays) {
    const textDays = therapyDurationDaysFromText(text);
    ptDurationDays = typeof textDays === 'number' ? String(textDays) : '[knee_pt_duration]';
  }

  const romFlexion = normalizeFlexionForNarrative(kneeMua?.rom_flexion) ||
    normalizeFlexionForNarrative(data?.rom_measurements?.flexion) ||
    normalizeFlexionForNarrative(extractKneeFlexionFromText(text)) ||
    '[knee_rom_flexion]';

  const adlImpact = normalizeAdlImpactSentence(kneeMua?.adl_impact) ||
    normalizeAdlImpactSentence((data?.functional_limitations || []).find(item => KNEE_MUA_FUNCTION_PATTERN.test(item))) ||
    normalizeAdlImpactSentence(sentenceContainingFunctionalLimitation(text)) ||
    '[knee_adl_impact]';

  return { ptDurationDays, romFlexion, adlImpact };
}

function sentenceContainingFunctionalLimitation(text: string): string | null {
  const sentences = text
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?])\s+/)
    .map(sentence => sentence.trim())
    .filter(Boolean);
  return sentences.find(sentence => KNEE_MUA_FUNCTION_PATTERN.test(sentence)) || null;
}
